import { theme } from 'antd';
import type { CSSProperties, ReactNode } from 'react';
import type { AccentHue } from '@/app/theme-tokens';
import { useAccent } from '@/hooks/use-accents';

interface Props {
  icon: ReactNode;
  hue: AccentHue;
  /** Cạnh ô (px): 24 trong menu · 28–32 ở tiêu đề · 40 trên thẻ tổng quan. */
  size?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * Ô nền nhạt bo tròn đặt sau một icon, màu theo khu vực (`hue`) — cách app «tô màu» cho icon mà không
 * làm icon mảnh khó nhìn: nền nhạt của tông đó, icon màu mực của tông đó (≥ 4,5:1, có test). Dùng ở menu,
 * tiêu đề màn hình, thẻ tổng quan. Nhận `className` vì Menu của Ant Design gắn lớp của nó lên icon.
 */
export function IconTile({ icon, hue, size = 28, className, style }: Props) {
  const { token } = theme.useToken();
  const accent = useAccent(hue);
  // Cỡ icon theo thang chữ: ô nhỏ 14, ô vừa 16, ô lớn 20.
  const iconSize =
    size >= 40 ? token.fontSizeHeading4 : size >= 30 ? token.fontSizeLG : token.fontSize;
  return (
    <span
      aria-hidden
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
        width: size,
        height: size,
        borderRadius: Math.max(token.borderRadiusXS, Math.round(size * 0.32)),
        background: accent.tile,
        color: accent.ink,
        fontSize: iconSize,
        lineHeight: 1,
        ...style,
      }}
    >
      {icon}
    </span>
  );
}
